import { forwardRef, useImperativeHandle, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import { cn } from '@/lib/utils.ts'

import { MentionInputComponent, getMentionSuggestionsStyle } from '@/components/Mention.tsx'
import { DEFAULT_COMMENT } from '@/constants/DEFAULT_VALUES.ts'
import { TEMPORARY_ID_PREFIX } from '@/constants/PREFIX.ts'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { useCommentMutation } from '@/requests/mutations/useMutateEntity.ts'
import { useMutateStory } from '@/requests/mutations/useMutateStory.ts'

type Props = {
  storyId: string
}

export const RejectionModal = forwardRef(({ storyId }: Props, ref) => {
  // biome-ignore lint/style/noNonNullAssertion: This is guaranteed to be non-null
  const user = useUserContext().user!
  const { create } = useCommentMutation(`${TEMPORARY_ID_PREFIX}comment-reject-status-${storyId}`)
  const { mutate } = useMutateStory(storyId)

  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')

  useImperativeHandle(
    ref,
    () => ({
      showModal: () => setOpen(true),
      close: () => setOpen(false),
    }),
    [],
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="w-full overflow-hidden [&>button:last-child]:hidden rounded-lg bg-surface-backgroundElevated py-4 px-0"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="w-full overflow-hidden">
          <DialogTitle className="font-EN-KR-sub-heading-bold text-contents-primary px-4">
            Reason for Rejection
          </DialogTitle>
          <DialogDescription className="sr-only" />
        </DialogHeader>
        <div className="w-full max-h-[80vh] overflow-scroll p-4">
          <MentionInputComponent
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={getMentionSuggestionsStyle({ fontSize: '14px', lineHeight: '16px' })}
            singleLine={false}
            placeholder="You can write the reason for the rejection here, but it's optional."
            allowSuggestionsAboveCursor
            classNames={{
              'mentions__control':
                'w-[450px] min-h-[100px] p-1 overflow-scroll font-EN-KR-body2-regular text-contents-primary bg-surface-backgroundElevated',
              'mentions__input': cn(
                '!font-EN-KR-body2-regular w-full p-2 rounded-xs overflow-scroll placeholder:!text-contents-placeholder',
                'resize-none whitespace-pre-wrap wrap-break-word',
                '!ring-0 !ring-offset-0 outline outline-1 outline-border-default-neutral focus-visible:!outline-border-default-focused',
              ),
            }}
            markup="**@__display__**"
          />
        </div>
        <DialogFooter className="px-4">
          <div className="flex w-full justify-between items-center">
            <DialogClose asChild>
              <Button type="button" variant="outline" className="font-EN-KR-body1-regular">
                Cancel
              </Button>
            </DialogClose>
            <DialogClose asChild>
              <Button
                type="button"
                variant="alert"
                className="font-EN-KR-body1-bold"
                onClick={() => {
                  if (value.trim().length === 0) {
                    mutate({ status: STORY_STATUSES.REJECTED })
                  } else {
                    create({ ...DEFAULT_COMMENT(storyId, user), content: value }).then(() => {
                      mutate({ status: STORY_STATUSES.REJECTED })
                      setValue('')
                    })
                  }
                }}
              >
                Reject
              </Button>
            </DialogClose>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
})
